import { CheckCircleOutlined } from '@ant-design/icons';
import React from 'react';
import { AIIcon } from '../../../assets/CustomIcons/AIIcon';
import CustomTooltip from '../../../components/UI/Tooltip/CustomTooltip';
import { TaskAttachment } from '../../../types/TaskAttachment';
import { EditableTaskFieldType } from '../field/EditableTaskFields';
import { RichTextEditor } from './editor/RichTextEditor';

export interface TaskDescriptionProps {
  description: string | undefined;
  disabledFields: EditableTaskFieldType[];
  onDescriptionChange: (value: string) => void;
  isDescriptionSaving: boolean;
  onImageUpload: (file: File) => Promise<TaskAttachment | null>;
  onCopyTaskDescriptionLink: () => void;
  onGenerateAISuggestion?: () => void;
  usersWithAccessToSpace?: Array<{
    id: number;
    fullName?: string | null;
    email: string;
    profilePicturePath?: string | null;
  }>;
}

export const TaskDescription: React.FC<TaskDescriptionProps> = React.memo(
  ({
    description,
    disabledFields,
    onDescriptionChange,
    isDescriptionSaving,
    onImageUpload,
    onCopyTaskDescriptionLink,
    onGenerateAISuggestion,
    usersWithAccessToSpace = [],
  }) => {
    return (
      <div className="flex flex-col gap-2 mt-6 mb-6">
        <div className="flex items-center justify-between gap-1">
          <div className="w-full flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-brand-grey-dark text-xs font-semibold">Description</span>
              {isDescriptionSaving && (
                <div className="flex gap-1 text-neutral-900 text-xs font-semibold">
                  <CheckCircleOutlined className="text-brand-taskonio-green" />
                  Saved
                </div>
              )}
            </div>
            {onGenerateAISuggestion && (
              <CustomTooltip
                title="Use AI to refine your description, or generate new one from scratch"
                overlayStyle={{ pointerEvents: 'auto' }}
              >
                <div
                  className="flex items-center gap-1 px-2 py-1 pl-1 rounded cursor-pointer relative"
                  style={{ background: 'linear-gradient(135deg, #7FF5AA 0%, #08CF65 100%)' }}
                  onClick={onGenerateAISuggestion}
                >
                  <div
                    className="absolute inset-0 rounded pointer-events-none"
                    style={{ boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.25) inset' }}
                  />
                  <AIIcon className="text-white" />
                  <span className="text-sm font-semibold text-white">Vibe Spec</span>
                </div>
              </CustomTooltip>
            )}
          </div>
        </div>
        <div className="w-full flex flex-col relative">
          <RichTextEditor
            value={description || ''}
            onChange={onDescriptionChange}
            usersWithAccessToSpace={usersWithAccessToSpace}
            placeholder="What this task is about?"
            disabled={disabledFields.includes('description')}
            onImageUpload={onImageUpload}
            onCopyTaskDescriptionLink={onCopyTaskDescriptionLink}
            minHeight="300px"
            mode="description"
          />
        </div>
      </div>
    );
  }
);
